"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.retry = exports.MAX_NUM_RETRY_REQUESTS = void 0;
const p_retry_1 = __importDefault(require("p-retry"));
const uuid_1 = require("uuid");
const errors_1 = require("./errors");
const json_utils_1 = require("./json.utils");
const MIN_RETRY_REQUESTS = 1;
exports.MAX_NUM_RETRY_REQUESTS = 6;
/**
 * {@link retry} requires that the thrown value is an instance of {@link Error} for it to work. Since any data type
 * can be thrown, this function converts an unknown thrown value into an error that extends {@link Error} so that
 * retry can work as expected.
 */
const wrapWithThrownError = (func) => async (attemptNumber) => {
    try {
        return await func(attemptNumber);
    }
    catch (err) {
        // We don't always capture all error information
        // so output everything to support debugging as
        // retry errors are often the hardest to debug
        // example: statusCode and statusMessage
        console.error(err);
        if (err instanceof Error) {
            throw err;
        }
        throw (0, errors_1.createError)('NonErrorThrownInRetry', (0, json_utils_1.toJson)(err));
    }
};
/**
 * Retry resolving a function until it resolves or the max number of retries is reached. Exponential back-off is used between retries. The formula
 * used to calculate the individual timeouts is: minTimeout * Math.pow(factor, attempt). If all retries fail, the last error is thrown.
 *
 * For example if minTimeout = 1000, exponent = 2, and numRetries = 6:
 *
 * - 1000 * 2^(1 - 1) = 1000 ms
 * - 1000 * 2^(2 - 1) = 2000 ms
 * - 1000 * 2^(3 - 1) = 4000 ms
 * - 1000 * 2^(4 - 1) = 8000 ms
 * - 1000 * 2^(5 - 1) = 16000 ms
 * - 1000 * 2^(6 - 1) = 32000 ms
 *
 * total = 63 seconds
 *
 * @param fn - The function to retry
 * @param options.errorMessage - The error message to include in log when a retry occurs.
 * @param options.numRetries - The number of times to retry the function. Defaults to 6.
 * @param options.minTimeout - The minimum timeout between retries. Defaults to 1000 ms.
 * @param options.exponent - The exponent to use when calculating the timeout between retries. Defaults to 2.
 * @param options.suppressErrors - If true, errors will not be logged to the console each time the function is retried. Defaults to false.
 * @param options.randomize - If true, the timeout between retries will be randomized. Defaults to false.
 * @returns The resolved value of the function
 * @throws The last error thrown by the function
 */
const retry = (fn, options = {}) => {
    const retries = options.numRetries ?? 6;
    if (retries < MIN_RETRY_REQUESTS || retries > exports.MAX_NUM_RETRY_REQUESTS) {
        throw new Error(`Configured max number of retries must be between ${MIN_RETRY_REQUESTS} and ${exports.MAX_NUM_RETRY_REQUESTS}`);
    }
    const minTimeout = options.minTimeout ?? 1000;
    const exponent = options.exponent ?? 2;
    const randomize = options.randomize ?? false;
    const retryTraceId = (0, uuid_1.v4)();
    return (0, p_retry_1.default)(wrapWithThrownError(fn), {
        retries,
        minTimeout,
        factor: exponent,
        randomize,
        onFailedAttempt: (error) => {
            if (options.suppressErrors) {
                return;
            }
            if (error.retriesLeft > 0) {
                const errorMessage = error.message ?? options.errorMessage ?? 'Unknown error';
                console.error('RetryAttemptFailure', `${errorMessage} attempt ${error.attemptNumber} of ${error.attemptNumber + error.retriesLeft} traceId ${retryTraceId} using function ${fn.toString()}`);
            }
        },
    });
};
exports.retry = retry;
